// Per-position display title for the steady-card breakdown list. The
// API ships positions as { pool.type, pool.pair, pool.protocol } —
// the breakdown row wants a human-readable label keyed off pool.type.
// `transformPosition` normalizes WALLET → "Wallet" / DEX_LP → "DEX LP"
// / TREASURY → "Treasury" etc. (see hooks/yaas/helpers.ts), so we
// switch on the normalized strings.
//
// Retail-pool note: as of the 2026-05 backend collapse, retail users'
// USDv balance is a TREASURY-type position shared with client-org
// treasury holdings (one row, two flows — retail-vs-org distinction
// lives in the position's owning account, not the pool). For retail
// users, the surface is the retail landing page, so TREASURY rows are
// always the user's own treasury holding. Labeled "Wallet Address" —
// note this DIVERGES from the backend's `buildPoolDisplayName`
// ("Account Balance"), which the label used to mirror. The row shows a
// destination address, and "Account Balance" collided with the section
// total directly above it. The "Wallet" branch stays as a defensive
// fallback for any pre-migration positions.
//
// LP rows named after a pair get an " LP" suffix ("USDC / USDv LP") only for
// the LP protocols the wallet reports as lpPosition.protocol. Every other
// DEX LP row keeps the bare pair.
const LP_SUFFIX_PROTOCOLS: ReadonlySet<string> = new Set(["raydium-clmm", "raydium-cpmm", "meteora-dlmm"]);

export function breakdownTitleFor(
    pool: { pair: string; protocol: string; type: string },
    lpProtocol?: string | null
): string {
    switch (pool.type) {
        case "Treasury":
        case "Wallet":
            return "Wallet Address";
        case "DEX LP":
            if (pool.pair && lpProtocol && LP_SUFFIX_PROTOCOLS.has(lpProtocol)) return `${pool.pair} LP`;
            return pool.pair || pool.protocol || "Position";
        case "Lending":
            return "Lending Protocol";
        case "Neobank":
            return "Neobank Wallet";
        default:
            return pool.pair || pool.protocol || "Position";
    }
}

// The wallet row's title: first four and last four characters, joined by
// "..". Same shape as the destination label (effectiveRouting).
export function shortAddress(address: string): string {
    return `${address.slice(0, 4)}..${address.slice(-4)}`;
}

// Cycling palette for the breakdown row's placeholder logo dot. Six
// tokens match the existing MOCK_BREAKDOWN_POSITIONS pattern; index
// mod 6 lets multi-position dashboards render distinct hues without
// per-pool icon assets.
const BREAKDOWN_LOGO_PALETTE = [
    "bg-graph-primary",
    "bg-graph-secondary",
    "bg-graph-tertiary",
    "bg-graph-quaternary",
    "bg-graph-quinary",
    "bg-graph-senary"
] as const;

export function breakdownLogoClassFor(index: number): string {
    return BREAKDOWN_LOGO_PALETTE[index % BREAKDOWN_LOGO_PALETTE.length];
}
